import { EnumUserModelAccessType } from "@/api";
import { PolicySyncJobStatus } from "@/types/api";

export const TRUSTLOGIX = "TRUSTLOGIX";
export const AGENT = "AGENT";
export const ASSET = "ASSET";
export const TRUE_STRING = "true";
export const ACTIVE = "ACTIVE";
export const INACTIVE = "INACTIVE";
export const IN_PROGRESS = "IN_PROGRESS";
export const SUCCEEDED = "SUCCEEDED";
export const FAILED = "FAILED";
export const CREATE_MASK_COLUMN_ACCESS_POLICY =
  "CREATE_MASK_COLUMN_ACCESS_POLICY";
export const VIEW_DETAILS = "VIEW_DETAILS";
export const CREATE_OR_VIEW_ACCESS_POLICY = "CREATE_OR_VIEW_ACCESS_POLICY";
export const REVOKE_UNUSED_ROLE = "REVOKE_UNUSED_ROLE";
export const REVOKE_IN_PROGRESS = "REVOKE_IN_PROGRESS";
export const GRANT_IN_PROGRESS = "GRANT_IN_PROGRESS";
export const VIEW_USER_DETAILS = "VIEW_USER_DETAILS";
export const VIEW_ROLE_DETAILS = "VIEW_ROLE_DETAILS";
export const VIEW_DATABASE_DETAILS = "VIEW_DATABASE_DETAILS";
export const VIEW_BUCKET_SETTINGS = "VIEW_BUCKET_SETTINGS";
export const EXFILTRATION_VIA_PUBLIC_BUCKET = "EXFILTRATION_VIA_PUBLIC_BUCKET";
export const DISABLE_USER = "DISABLE_USER";
export const CREATE_TICKET = "CREATE_TICKET";
export const WHITELIST_APPLICATION = "WHITELIST_APPLICATION";
export const WHITELIST_IP_ADDRESS = "WHITELIST_IP_ADDRESS";
export const WHITELIST_USER = "WHITELIST_USER";
export const CLASSIFY_DATA = "CLASSIFY_DATA";
export const DISMISS_ALERT = "DISMISS_ALERT";
export const SUPPRESS_ALERT = "SUPPRESS_ALERT";

export const OR = "OR";
export const AND = "AND";
export const ARRAY = "ARRAY";
export const ROLE = "ROLE";
export const USER = "USER";
export const GROUP = "GROUP";
export const SERVICE_PRINCIPAL = "SERVICE_PRINCIPAL";
export const USER_ATTRIBUTE = "USER_ATTRIBUTE";
export const DATA_ATTRIBUTE = "DATA_ATTRIBUTE";
export const FUNCTION = "FUNCTION";
export const CUSTOM_EXPRESSION = "CUSTOM_EXPRESSION";
export const COLUMN_TAGGED_AS = "COLUMN_TAGGED_AS";
export const OBJECTS_TAGGED_AS = "OBJECTS_TAGGED_AS";
export const COLUMN_NAMES = "COLUMN_NAMES";
export const CONSTANT_USER_ATTRIBUTE_VALUE = "CONSTANT_USER_ATTRIBUTE_VALUE";
export const ATTRIBUTE_FILTER = "ATTRIBUTE_FILTER";
export const DIMENSION_USER_ATTRIBUTE = "DIMENSION_USER_ATTRIBUTE";
export const POWER_BI_PREFIX = "PBI_";
export const RESOURCE_ATTRIBUTE = "RESOURCE_ATTRIBUTE";
export const REQUEST_ATTRIBUTE = "REQUEST_ATTRIBUTE";
export const AGENT_ATTRIBUTE = "AGENT_ATTRIBUTE";
export const PURPOSE = "PURPOSE";

/**
 * `name` of the Sensitivity attribute in the RESOURCE identity payload — its
 * `displayName` is "Sensitivity". On a Resource Access policy this is the one
 * attribute whose values are scoped to the labels picked on the Policy Scope
 * step, so the RHS dropdown filters its master data against `FormField.tags`.
 */
export const SENSITIVITY = "SENSITIVITY";

/**
 * Condition-attribute group name -> `identityType` for
 * `getUserAttributesMetadataUsingGet`. The single flip point if the backend
 * names the resource identity `ASSET` (which is what MCP and Enterprise
 * Policies call it) rather than `RESOURCE`.
 *
 * `USER_ATTRIBUTE` is deliberately absent: it keeps its account-scoped
 * `useConditionUserAttributes` call, which carries `isMasking` / `dataProduct` /
 * `serverName` semantics the identity-scoped call has no equivalent for.
 */
export const IDENTITY_TYPE_BY_ELEMENT_TYPE: Partial<Record<string, string>> = {
  [AGENT_ATTRIBUTE]: "AGENT",
  [RESOURCE_ATTRIBUTE]: "RESOURCE",
};

/**
 * Groups whose "Value" match-with stores one of the selected attribute's
 * master-data values (`/api/user_entitlement_attribute/masterdata/{attributeId}`).
 *
 * The render side additionally requires the account context for the two identity
 * groups — see `fnConditionBuilderIsMasterDataValueLhs`. Both the wizard and the
 * classic form consume the `{ value }` shape this list produces
 * (`ValueAPIComponent` / `AttributeValueApiField`, `dataItemKey="value"`). The load side
 * (`generateConditions`) stays context-free: no application-context matrix
 * offers a "Value" match-with for these groups.
 */
export const MASTER_DATA_VALUE_LHS_GROUPS = [
  USER_ATTRIBUTE,
  AGENT_ATTRIBUTE,
  RESOURCE_ATTRIBUTE,
];

export const ElementsWithChildren = [
  USER_ATTRIBUTE,
  DATA_ATTRIBUTE,
  ATTRIBUTE_FILTER,
  COLUMN_TAGGED_AS,
  FUNCTION,
  RESOURCE_ATTRIBUTE,
  AGENT_ATTRIBUTE,
];

export const ADDITIONAL_FILTER_SUPPORTED_VALUE_TYPES = [
  CONSTANT_USER_ATTRIBUTE_VALUE,
  DIMENSION_USER_ATTRIBUTE,
];

export const PRINCIPALS = {
  ROLE,
  USER,
  GROUP,
  SERVICE_PRINCIPAL,
};

export const PRINCIPALS_LIST = [PRINCIPALS.USER, PRINCIPALS.SERVICE_PRINCIPAL];

export const OBJECT_NAMES = "OBJECT_NAMES";

export const VALUE = "Value";

export const CONSTANT = "CONSTANT";
export const SESSION_ATTRIBUTE = "SESSION_ATTRIBUTE";

// NOTE: This is used as a RHS valueType/element-type in condition builders.
export const ELEMENT_TYPE_VALUE = "value";

export const DEFAULT_RULES_CONDITION_ID = "2";

export const NO_DARK_DATA_AVAILABLE = "NO_DARK_DATA_AVAILABLE";

export const ColumnTypeMap = {
  VARCHAR: "STRING",
  STRING: "STRING",
  TEXT: "STRING",
  NUMBER: "NUMBER",
  DECIMAL: "NUMBER",
  NUMERIC: "NUMBER",
  INT: "NUMBER",
  INTEGER: "NUMBER",
  BIGINT: "NUMBER",
  SMALLINT: "NUMBER",
  TINYINT: "NUMBER",
  BYTEINT: "NUMBER",
  FLOAT: "FLOAT",
  FLOAT5: "FLOAT",
  FLOAT8: "FLOAT",
  DOUBLE: "FLOAT",
  "DOUBLE PRECISION": "FLOAT",
  REAL: "FLOAT",
  Boolean: "BOOLEAN",
};

export const OBJECT_SELECTIONS = {
  WAREHOUSE: "WAREHOUSE",
  DATABASE: "DATABASE",
  SCHEMA: "SCHEMA",
  APPLICATION: "APPLICATION",
  APPLICATION_ROLE: "APPLICATION ROLE",
  DBT_PROJECT: "DBT_PROJECT",
  APP: "APP",
  NOTEBOOK: "NOTEBOOK",
} as const;

export const AllOption = { name: "ALL", displayName: "All" };
export const DatasourceAllOption = { id: "ALL", name: "ALL" };

export const USER_NAME = "USER_NAME";
export const DATA_PRODUCT = "DATA_PRODUCT";
export const DATA_SOURCE = "DATA_SOURCE";
export const COLUMN = "COLUMN";
export const NORMAL_ATTRIBUTE = "NORMAL_ATTRIBUTE";

export const SUPPORTED_FILED_DATA_SIZE = "DATA_SIZE";

export const QUOTES_REGEX = /['"]/;

// Regular expression to match names that don't start with a number or special character
export const NAME_VALIDATION_REGEX = /^[^0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/;

export const QUERY_KEYS = {
  getUserAttributesMetadataUsingGet: {
    ColumnRuleItem: "getUserAttributesMetadataUsingGet.ColumnRuleItem",
    RuleValue: "getUserAttributesMetadataUsingGet.RuleValue",
  },
  getAdditionalColumnConditionForAttributeUsingGet: {
    ColumnRuleItem:
      "getAdditionalColumnConditionForAttributeUsingGet.ColumnRuleItem",
  },
  getTagsUsingGet2: {
    useTags: "getTagsUsingGet2.userTags",
    useDomainPolicyTags: "getTagsUsingGet2.useDomainPolicyTags",
  },
  getTlxAlertsUsingGet: {
    AIRiskCards: "getTlxAlertsUsingGet",
    AIRiskCardsList: "getTlxAlertsUsingGet.AIRiskCardsList",
  },
};

export const UserAttributeValueColumnsCacheKey = "userValuesColumns";

export const FILTER_STATE_KEY = "generic:filters";

export const TAG_ASSOCIATION_OBJECT_TYPES = {
  DATABASE: "DATABASE",
  SCHEMA: "SCHEMA",
  TABLE: "TABLE",
  COLUMN: "COLUMN",
};

// Page size used when fetching entitlements
export const ENTITLEMENT_PAGE_SIZE = 2000;

// users page
export const ROLE_IDS = {
  SUPER_USER: "1", // 1 is the ID for Super User
  DATA_SOURCE_ADMINISTRATOR: "7", // 7 is the ID for Data Source Administrator
  COMPLIANCE_MANAGER: "2", // 2 is the ID for Compliance Manager
};
export const DEFAULT_ACCESS_TYPE = EnumUserModelAccessType.DATA_SOURCE;
export const ALL_CONTEXT_VALUE = "ALL";

// Error codes
export const CONSENT_REQUIRED = "CONSENT_REQUIRED";

export const POLICY_SYNC_JOB_STATUS: Record<string, PolicySyncJobStatus> = {
  EXECUTED: "EXECUTED",
  IN_PROGRESS: "IN_PROGRESS",
  INITIATED: "INITIATED",
  REMOVED: "REMOVED",
};

export const POLICY_TYPES = {
  DataMasking: "12",
  DataObjectAccess: "9",
  RowAccessPolicy: "10",
  // Resource Access Policy — scopes SharePoint content by Microsoft Purview
  // sensitivity labels instead of database objects.
  ResourceAccess: "16",
};

/**
 * Policy types the classic single-page form cannot represent. Its step 2 is
 * hard-wired to column/table rules (DomainPolicy/Form/DataRule.tsx), so these
 * types have no condition options, no reachable policySubtype, and no save
 * mapping there. The wizard is the only flow that can create or edit them.
 */
export const WIZARD_ONLY_POLICY_TYPES = new Set<string>([
  POLICY_TYPES.ResourceAccess,
]);

export const ICEBERG = "ICEBERG";

/**
 * Freshness window for the condition builder's attribute-metadata queries
 * (`useConditionUserAttributes`). Load-bearing, not an optimisation — see the
 * comment on that hook. `useClearUserAttributesMetadataQuery` is the escape
 * hatch that invalidates these entries before the window elapses, so the two
 * must be changed together.
 */
export const CONDITION_ATTRIBUTES_STALE_TIME = 5 * 60 * 1000;

/** The public TrustLogix documentation site and its release notes. */
export const TRUSTLOGIX_DOCS_URL = "https://docs.trustlogix.io/";
export const TRUSTLOGIX_RELEASE_NOTES_URL =
  "https://docs.trustlogix.io/release-notes";
